import { ExternalLink, FileCode2 } from "lucide-react";
import type { ReactNode } from "react";

interface FileReferenceToken {
  kind: "file";
  start: number;
  end: number;
  label: string;
  path: string;
  line?: number;
}

interface ExternalReferenceToken {
  kind: "external";
  start: number;
  end: number;
  label: string;
  url: string;
}

export type ReviewCommentReference = FileReferenceToken | ExternalReferenceToken;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function lineFromMatch(match: RegExpMatchArray) {
  const value = match[1] ?? match[2];
  if (!value) return undefined;
  const line = Number(value);
  return Number.isSafeInteger(line) && line > 0 ? line : undefined;
}

function unwrapCodeTicks(body: string, token: FileReferenceToken): FileReferenceToken {
  if (body[token.start - 1] !== "`" || body[token.end] !== "`") return token;
  return { ...token, start: token.start - 1, end: token.end + 1 };
}

export function findReviewCommentReferences(body: string, codePaths: string[]): ReviewCommentReference[] {
  const paths = [...new Set(codePaths)].sort((left, right) => right.length - left.length);
  const pathSet = new Set(paths);
  const candidates: ReviewCommentReference[] = [];

  for (const match of body.matchAll(/\[([^\]]+)]\((https?:\/\/[^)\s]+)\)/gi)) {
    const url = match[2]!;
    let target: { path: string; line?: number } | undefined;
    try {
      const parsed = new URL(url);
      if (parsed.hostname === "github.com") {
        const decoded = decodeURIComponent(parsed.pathname);
        const path = paths.find((candidate) => decoded.endsWith(`/${candidate}`));
        const anchor = parsed.hash.match(/^#L(\d+)/i);
        if (path) target = { path, line: anchor ? Number(anchor[1]) : undefined };
      }
    } catch {
      // The generic external-link token below will still make a malformed URL inert.
    }
    const start = match.index!;
    candidates.push(target
      ? { kind: "file", start, end: start + match[0].length, label: match[1]!, ...target }
      : { kind: "external", start, end: start + match[0].length, label: match[1]!, url });
  }

  for (const match of body.matchAll(/https?:\/\/[^\s<>()]+/gi)) {
    const url = match[0].replace(/[.,;:!?]+$/, "");
    let target: { path: string; line?: number } | undefined;
    try {
      const parsed = new URL(url);
      if (parsed.hostname === "github.com") {
        const decoded = decodeURIComponent(parsed.pathname);
        const path = paths.find((candidate) => decoded.endsWith(`/${candidate}`));
        const anchor = parsed.hash.match(/^#L(\d+)/i);
        if (path) target = { path, line: anchor ? Number(anchor[1]) : undefined };
      }
    } catch {
      // Render it as ordinary prose when URL parsing fails.
    }
    const start = match.index!;
    candidates.push(target
      ? { kind: "file", start, end: start + url.length, label: target.path.split("/").at(-1) ?? target.path, ...target }
      : { kind: "external", start, end: start + url.length, label: url, url });
  }

  for (const path of paths) {
    const expression = new RegExp(`${escapeRegExp(path)}(?:(?::|#L)(\\d+)(?:-L?(\\d+))?)?`, "g");
    for (const match of body.matchAll(expression)) {
      const start = match.index!;
      candidates.push(unwrapCodeTicks(body, {
        kind: "file",
        start,
        end: start + match[0].length,
        label: match[0],
        path,
        line: lineFromMatch(match),
      }));
    }
  }

  const pathsByFileName = new Map<string, string[]>();
  for (const path of paths) {
    const fileName = path.split("/").at(-1)!;
    pathsByFileName.set(fileName, [...(pathsByFileName.get(fileName) ?? []), path]);
  }
  for (const [fileName, matchingPaths] of pathsByFileName) {
    if (matchingPaths.length !== 1 || pathSet.has(fileName)) continue;
    const expression = new RegExp(`(^|[^\\w./-])(${escapeRegExp(fileName)})(?:(?::|#L)(\\d+)(?:-L?(\\d+))?)?`, "g");
    for (const match of body.matchAll(expression)) {
      const prefix = match[1] ?? "";
      const label = `${match[2]}${match[3] ? `:${match[3]}` : ""}`;
      const start = match.index! + prefix.length;
      candidates.push(unwrapCodeTicks(body, {
        kind: "file",
        start,
        end: start + label.length,
        label,
        path: matchingPaths[0]!,
        line: match[3] ? Number(match[3]) : undefined,
      }));
    }
  }

  const accepted: ReviewCommentReference[] = [];
  for (const candidate of candidates.sort((left, right) => left.start - right.start || right.end - left.end)) {
    if (accepted.some((existing) => candidate.start < existing.end && candidate.end > existing.start)) continue;
    accepted.push(candidate);
  }
  return accepted.sort((left, right) => left.start - right.start);
}

export function ReviewCommentBody({
  body,
  codePaths,
  onSelectLocation,
}: {
  body: string;
  codePaths: string[];
  onSelectLocation: (path: string, line?: number) => void;
}) {
  const references = findReviewCommentReferences(body, codePaths);
  if (references.length === 0) return <>{body || "Open this comment on GitHub."}</>;
  const contents: ReactNode[] = [];
  let cursor = 0;
  for (const reference of references) {
    if (reference.start > cursor) contents.push(body.slice(cursor, reference.start));
    if (reference.kind === "file") {
      contents.push(
        <button
          className="review-comment-reference"
          key={`${reference.start}:${reference.path}`}
          onClick={() => onSelectLocation(reference.path, reference.line)}
          title={`Open ${reference.path}${reference.line ? ` at line ${reference.line}` : ""}`}
          type="button"
        ><FileCode2 aria-hidden="true" /><span>{reference.label.replaceAll("`", "")}</span></button>,
      );
    } else {
      contents.push(
        <a className="review-comment-external" href={reference.url} key={`${reference.start}:${reference.url}`} rel="noreferrer" target="_blank">
          <span>{reference.label}</span><ExternalLink aria-hidden="true" />
        </a>,
      );
    }
    cursor = reference.end;
  }
  if (cursor < body.length) contents.push(body.slice(cursor));
  return <>{contents}</>;
}
